class VelocityTheme {
  constructor() {
    this.routesRoot = window.Shopify?.routes?.root || '/';
    this.toastTimer = null;

    if (!this.routesRoot.endsWith('/')) {
      this.routesRoot += '/';
    }

    this.bindMobileMenu();
    this.bindQuantitySelectors();
    this.bindQuickAdd();
    this.prepareCartToast();
  }

  bindMobileMenu() {
    const button = document.querySelector('[data-mobile-menu-button]');
    const menu = document.querySelector('[data-mobile-menu]');

    if (!button || !menu) return;

    const closeMenu = (restoreFocus = false) => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      menu.inert = true;
      button.setAttribute('aria-expanded', 'false');

      if (restoreFocus) {
        button.focus();
      }
    };

    const openMenu = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      menu.inert = false;
      button.setAttribute('aria-expanded', 'true');
    };

    closeMenu();

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) {
        closeMenu();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (button.getAttribute('aria-expanded') !== 'true') return;

      closeMenu(true);
    });

    document.addEventListener('click', (event) => {
      if (button.getAttribute('aria-expanded') !== 'true') return;
      if (button.contains(event.target) || menu.contains(event.target)) return;

      closeMenu();
    });

    const desktopBreakpoint = window.matchMedia('(min-width: 990px)');

    desktopBreakpoint.addEventListener('change', (event) => {
      if (event.matches) {
        closeMenu();
      }
    });
  }

  bindQuantitySelectors() {
    document.addEventListener('click', (event) => {
      const control = event.target.closest('[data-quantity-control]');

      if (!control) return;

      event.preventDefault();

      const wrapper = control.closest('[data-quantity-wrapper]');
      const input = wrapper?.querySelector('input[type="number"]');

      if (!wrapper || !input || input.disabled) return;

      const direction =
        control.dataset.quantityControl === 'plus' ? 1 : -1;

      const currentValue = Number.parseInt(input.value, 10);
      const minimumValue = Number.parseInt(input.min, 10);
      const maximumValue = Number.parseInt(input.max, 10);
      const stepValue = Number.parseInt(input.step, 10);

      const current = Number.isFinite(currentValue) ? currentValue : 1;
      const minimum = Number.isFinite(minimumValue) ? minimumValue : 1;
      const maximum = Number.isFinite(maximumValue)
        ? maximumValue
        : Number.POSITIVE_INFINITY;
      const step = Number.isFinite(stepValue) && stepValue > 0
        ? stepValue
        : 1;

      const nextValue = Math.min(
        maximum,
        Math.max(minimum, current + direction * step)
      );

      input.value = String(nextValue);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindQuickAdd() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.quick-add-form');

      if (!form) return;

      event.preventDefault();

      if (form.dataset.loading === 'true') return;

      const button = form.querySelector('button[type="submit"]');
      const originalText = button?.textContent || 'Add to cart';

      form.dataset.loading = 'true';
      form.setAttribute('aria-busy', 'true');

      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';
      }

      try {
        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });

        const responseData = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          const errorMessage =
            responseData.description ||
            responseData.message ||
            'We could not add this item to your bag.';

          throw new Error(errorMessage);
        }

        await this.refreshCartCount();
        this.showToast('Added to your bag');
      } catch (error) {
        this.showToast(this.getCartErrorMessage(error), true);
      } finally {
        delete form.dataset.loading;
        form.removeAttribute('aria-busy');

        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
        }
      }
    });
  }

  async refreshCartCount() {
    try {
      const response = await fetch(`${this.routesRoot}cart.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to refresh cart');
      }

      const cart = await response.json();
      const itemCount = Number(cart.item_count || 0);
      const itemLabel = itemCount === 1 ? 'item' : 'items';

      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(itemCount);
      });

      document.querySelectorAll('.header__cart').forEach((link) => {
        link.setAttribute(
          'aria-label',
          `Cart, ${itemCount} ${itemLabel}`
        );
      });

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart
          }
        })
      );
    } catch (error) {
      return;
    }
  }

  getCartErrorMessage(error) {
    const rawMessage =
      error instanceof Error
        ? error.message
        : 'We could not add this item to your bag.';

    const cleanMessage = String(rawMessage)
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleanMessage || cleanMessage === 'Cart add failed') {
      return 'We could not add this item to your bag. Please try again.';
    }

    return cleanMessage.slice(0, 180);
  }

  prepareCartToast() {
    const toast = document.querySelector('[data-cart-toast]');

    if (!toast) return;

    toast.setAttribute('aria-atomic', 'true');
  }

  showToast(message, isError = false) {
    const toast = document.querySelector('[data-cart-toast]');

    if (!toast) return;

    window.clearTimeout(this.toastTimer);

    toast.classList.remove('is-visible', 'is-error');
    toast.textContent = '';
    toast.setAttribute(
      'aria-live',
      isError ? 'assertive' : 'polite'
    );

    window.requestAnimationFrame(() => {
      toast.textContent = message;
      toast.classList.toggle('is-error', isError);
      toast.classList.add('is-visible');

      this.toastTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible', 'is-error');

        window.setTimeout(() => {
          if (!toast.classList.contains('is-visible')) {
            toast.textContent = '';
          }
        }, 300);
      }, 3000);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new VelocityTheme();
});